import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Observable, firstValueFrom, lastValueFrom, map, startWith } from "rxjs";
import { AddSafeBoxProductSku, CampaignsSafeBoxAdminApiClient, CampaignsProductAdminApiClient, ConsumptionValidationToolEnum, FilterValidationToolOperatorEnum } from "src/app/api/api-campaigns.service.generated";
import { MessageDialogModel } from "src/app/common/message-dialog/message-dialog.component";
import { ButtonComponent } from "wecheer-common";
import { MatIconModule } from "@angular/material/icon";

@Component({
    selector: 'app-safe-box-edit',
    templateUrl: './safe-box-edit.component.html',
    styleUrls: ['./safe-box-edit.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        MatChipsModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatIconModule,
        ButtonComponent
    ]
})

export class SafeBoxEditComponent implements OnInit {
    private dialog = inject(MatDialog);
    private dialogRef = inject<MatDialogRef<SafeBoxEditComponent, AddSafeBoxProductSku | undefined>>(MatDialogRef);
    private safeBoxAdminApi = inject(CampaignsSafeBoxAdminApiClient);
    private campaignsProductAdminApi = inject(CampaignsProductAdminApiClient);
    private snackBar = inject(MatSnackBar);
    editModel = inject<AddSafeBoxProductSku>(MAT_DIALOG_DATA);


    static config = { width: '600px' };

    form: FormGroup = new FormGroup({
        productSkuId: new FormControl('', Validators.required),
        numberOfCharacters: new FormControl({ value: '', disabled: true }),
        format: new FormControl('', Validators.required),
        numberOfSafeBoxes: new FormControl('', [Validators.required, Validators.max(1000000)]),
        tags: new FormControl(null)
    });

    skuIdControl = this.form.controls.productSkuId as FormControl;
    tagsControl = this.form.controls.tags as FormControl;

    filteredOptions: Observable<any[]>;

    isLoading = false;
    allowedValidationTools: ConsumptionValidationToolEnum[] = [
        ConsumptionValidationToolEnum.SecretCode,
        ConsumptionValidationToolEnum.UniqueQR
    ];

    tagList = [];
    addOnBlur = true;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    productSKUList = [];

    async ngOnInit() {
        await this.getProductList();

        this.initSkuFilter();

        this.form.controls.tags.valueChanges.subscribe(value => {
            const maxlength = 300;
            if (value.toString().length > maxlength) {
                this.form.controls.tags.setErrors({ maxlengInvalid: maxlength });
            }
        })
    }

    private initSkuFilter() {
        this.filteredOptions = this.skuIdControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                let name: string;
                if (typeof value === 'string') {
                    name = value;
                    this.form.controls.productSkuId.setErrors({ unselected: true })
                } else {
                    name = value?.name
                }
                return name;
            }),
            map(name => name ? this._filter(name || '') : this.productSKUList.slice())
        );
    }

    displayFn(product: any): string {
        return product && product.name ? product.name : '';
    }

    private _filter(name: string): any[] {
        const filterValue = name.toLowerCase();

        return this.productSKUList.filter(option => option.name.toLowerCase().includes(filterValue));
    }

    async onSubmit() {
        if (this.form.invalid) {
            return;
        }

        const model = new AddSafeBoxProductSku({
            format: this.form.value.format,
            productSkuName: this.form.value.productSkuId.name,
            numberOfCharacters: this.form.value.numberOfCharacters,
            numberOfSafeboxToCreate: this.form.value.numberOfSafeBoxes,
            productSkuId: this.form.value.productSkuId.id,
            tags: this.form.value.tags && this.form.value.tags.toString()
        });

        this.isLoading = true;
        try {
            await lastValueFrom(this.safeBoxAdminApi.createSafeBoxProductSku(model));

            this.snackBar.open('Codes was created', null, { duration: 3000 });
            this.dialogRef.close(undefined);

        } catch (err: any) {
            if (err.status === 400 && err.response)
                new MessageDialogModel(err.response).show(this.dialog);
        }
        this.isLoading = false;
    }

    onFormatKeyUpEvent(event: any) {
        const regx = /[a-zA-Z0-9]/;
        const regRes = regx.test(event.target.value);
        if (regRes) {
            event.preventDefault();
            event.target.value = event.target.value.replace(regx, '');
        }

        let formatValue = event.target.value;

        if (!formatValue) {
            this.form.controls['numberOfCharacters'].setValue(0);
            return;
        }

        if (formatValue.indexOf('*') < 0) {
            this.form.controls['numberOfCharacters'].setValue(0);
            return;
        }

        let numberOfCharacters = formatValue.split('*').length - 1;
        this.form.controls['numberOfCharacters'].setValue(numberOfCharacters);
    }

    async getProductList() {
        const productResponse = await firstValueFrom(this.campaignsProductAdminApi.getProducts(this.allowedValidationTools, FilterValidationToolOperatorEnum.Contains));
        if (productResponse.length > 0) {
            for (let index = 0; index < productResponse.length; index++) {
                const matchSkus = productResponse[index].skus?.filter(s => s.consumptionValidationTools
                    && s.consumptionValidationTools.some(v => v.validationToolEnum && this.allowedValidationTools.includes(ConsumptionValidationToolEnum[v.validationToolEnum])));
                if (matchSkus && matchSkus.length > 0) {
                    this.productSKUList = this.productSKUList.concat(matchSkus);
                }
            }
        }
    }

    onClose(): void {
        this.dialogRef.close(undefined);
    }

    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        // Add our fruit
        if (value) {
            this.tagList.push(value);
            this.form.controls.tags.setValue(this.tagList);
            this.form.controls.tags.markAsTouched();
        }

        // Clear the input value
        event.chipInput!.clear();
    }

    remove(tag: string): void {
        const index = this.tagList.indexOf(tag);

        if (index >= 0) {
            this.tagList.splice(index, 1);
            this.form.controls.tags.setValue(this.tagList);
        }
    }
}